import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Bell,
  Calendar,
  Settings,
  ScrollText,
  LogOut,
  Menu,
  X,
  ChevronRight,
  KeyRound,
} from 'lucide-react';
import clsx from 'clsx';
import { UserRole } from '@barrikadave/shared';
import { useAuth } from '../hooks/useAuth';
import Badge, { getRoleBadgeVariant } from './Badge';
import { authApi, usersApi } from '../services/api';

// `adminOnly` items are visible only to ADMIN.
// `staffAllowed` items are visible to both ADMIN and STAF
// (STAF can manage users but not change settings/notifications/activity).
const allNavItems = [
  { to: '/dashboard', label: 'Paneli Kryesor', icon: LayoutDashboard, adminOnly: false, staffAllowed: true },
  { to: '/users', label: 'Perdoruesit', icon: Users, adminOnly: true, staffAllowed: true },
  { to: '/posts', label: 'Postimet', icon: FileText, adminOnly: false, staffAllowed: true },
  { to: '/calendar', label: 'Kalendar', icon: Calendar, adminOnly: false, staffAllowed: true },
  { to: '/notifications', label: 'Njoftimet', icon: Bell, adminOnly: true, staffAllowed: false },
  { to: '/activity', label: 'Regjistri', icon: ScrollText, adminOnly: true, staffAllowed: false },
  { to: '/settings', label: 'Cilesimet', icon: Settings, adminOnly: true, staffAllowed: false },
];

const pageTitles: Record<string, string> = {
  '/dashboard': 'Paneli Kryesor',
  '/users': 'Menaxhimi i Perdoruesve',
  '/posts': 'Menaxhimi i Postimeve',
  '/calendar': 'Kalendari i Postimeve',
  '/notifications': 'Njoftimet',
  '/activity': 'Regjistri i Aktiviteteve',
  '/settings': 'Cilesimet',
};

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState(false);
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const closePwdModal = () => {
    setPwdOpen(false);
    setPwdCurrent('');
    setPwdNew('');
    setPwdConfirm('');
    setPwdError(null);
    setPwdSuccess(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    setPwdSuccess(false);
    if (pwdNew.length < 8) {
      setPwdError('Fjalekalimi i ri duhet te kete te pakten 8 karaktere.');
      return;
    }
    if (pwdNew !== pwdConfirm) {
      setPwdError('Fjalekalimet nuk perputhen.');
      return;
    }
    setPwdSubmitting(true);
    try {
      await authApi.changePassword(pwdCurrent, pwdNew);
      setPwdSuccess(true);
      setPwdCurrent('');
      setPwdNew('');
      setPwdConfirm('');
    } catch (err) {
      setPwdError(err instanceof Error ? err.message : 'Deshtoi.');
    } finally {
      setPwdSubmitting(false);
    }
  };
  const isAdmin = user?.role === UserRole.ADMIN;
  const isStaff = user?.role === UserRole.STAF;

  const { data: pendingUsers } = useQuery({
    queryKey: ['users-pending-count'],
    queryFn: usersApi.countPending,
    refetchInterval: 60_000,
    enabled: isAdmin || isStaff,
  });
  const pendingCount = pendingUsers?.count ?? 0;

  const navItems = allNavItems.filter((item) => {
    if (!item.adminOnly) return true;
    if (isAdmin) return true;
    if (isStaff && item.staffAllowed) return true;
    return false;
  });

  const pageTitle =
    pageTitles[location.pathname] ||
    (location.pathname.startsWith('/activity/') ? 'Aktiviteti i Perdoruesit' : 'Rruga e Barrikadave Admin');

  const breadcrumbSegments = location.pathname.split('/').filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 flex flex-col transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-barrikadave-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">R</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-barrikadave-500 leading-none">Rruga e Barrikadave</h1>
              <p className="text-[10px] text-gray-400 leading-none mt-0.5">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                clsx('sidebar-link', isActive && 'sidebar-link-active')
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span>{item.label}</span>
              {item.to === '/users' && pendingCount > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold">
                  {pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        {user && (
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-barrikadave-100 flex items-center justify-center shrink-0">
                <span className="text-barrikadave-600 font-semibold text-sm">
                  {user.firstName[0]}
                  {user.lastName[0]}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.firstName} {user.lastName}
                </p>
                <Badge variant={getRoleBadgeVariant(user.role)}>{user.role}</Badge>
              </div>
            </div>
            <button
              onClick={() => setPwdOpen(true)}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-500 hover:text-barrikadave-600 hover:bg-barrikadave-50 rounded-lg transition-colors"
            >
              <KeyRound className="h-4 w-4" />
              <span>Ndrysho fjalekalimin</span>
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Dil</span>
            </button>
          </div>
        )}
      </aside>

      {/* Change Password Modal */}
      {pwdOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4">
          <form
            onSubmit={handleChangePassword}
            className="w-full max-w-md bg-white rounded-xl shadow-xl p-6"
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-semibold text-gray-900">
                Ndrysho fjalekalimin
              </h3>
              <button
                type="button"
                onClick={closePwdModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Mbyll"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Fjalekalimi i ri duhet te kete te pakten 8 karaktere.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fjalekalimi aktual
            </label>
            <input
              type="password"
              autoComplete="current-password"
              className="input-field mb-3"
              value={pwdCurrent}
              onChange={(e) => setPwdCurrent(e.target.value)}
              required
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fjalekalimi i ri
            </label>
            <input
              type="password"
              autoComplete="new-password"
              className="input-field mb-3"
              value={pwdNew}
              onChange={(e) => setPwdNew(e.target.value)}
              minLength={8}
              required
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Konfirmo fjalekalimin e ri
            </label>
            <input
              type="password"
              autoComplete="new-password"
              className="input-field mb-3"
              value={pwdConfirm}
              onChange={(e) => setPwdConfirm(e.target.value)}
              minLength={8}
              required
            />

            {pwdError && (
              <p className="text-sm text-red-600 mb-2">{pwdError}</p>
            )}
            {pwdSuccess && (
              <p className="text-sm text-green-600 mb-2">
                Fjalekalimi u ndryshua me sukses.
              </p>
            )}

            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={closePwdModal}
                className="btn-ghost"
              >
                Mbyll
              </button>
              <button
                type="submit"
                disabled={pwdSubmitting}
                className="btn-primary"
              >
                {pwdSubmitting ? 'Duke ruajtur...' : 'Ruaj'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-8 gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex items-center text-sm text-gray-500">
            <span className="text-gray-400">Admin</span>
            {breadcrumbSegments.map((segment, i) => (
              <span key={i} className="flex items-center">
                <ChevronRight className="h-3 w-3 mx-1 text-gray-300" />
                <span
                  className={
                    i === breadcrumbSegments.length - 1
                      ? 'text-gray-900 font-medium'
                      : 'text-gray-400'
                  }
                >
                  {pageTitles[`/${breadcrumbSegments.slice(0, i + 1).join('/')}`] ||
                    segment.charAt(0).toUpperCase() + segment.slice(1)}
                </span>
              </span>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <h2 className="text-sm font-medium text-gray-700 hidden sm:block">
              {pageTitle}
            </h2>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
